import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { formatDate } from "./formatDate";

dayjs.extend(customParseFormat);

const nullIfEmpty = (value: string | undefined): string | null => {
  if(!value) return null;
  return (value.length > 0) ? value : null;
};

const withArticle = (value: string): string => {
  const vowels = ['a', 'e', 'i', 'o', 'u']
  const firstLetter = value.trim()[0].toLowerCase();

  const article = vowels.includes(firstLetter) ? 'an' : 'a';
  return `${article} ${value}`;
};

const parseCommaSeparatedList = (list: string): string[] => {
  return list
    .split(",")
    .map(element => element.trim())
    .filter(element => element.length > 0);
};

const truncate = (str: string, maxLength: number): string => {
  return str.length > maxLength ? str.slice(0, maxLength) + '...' : str;
}

const getTodaysDate = () => {
  return formatDate(new Date().toISOString()).date;
};

const fullName = (firstName?:string, lastName?: string) => {
  if (firstName && lastName) return firstName + ' ' + lastName;
  if (firstName) return firstName;
  if (lastName) return lastName;
  return '';
};

const areSetsEqual = (setA: Set<any>, setB: Set<any>): boolean => {
  if (setA.size !== setB.size) {
    return false;
  }
  for (const item of setA) {
    if (!setB.has(item)) {
      return false;
    }
  }
  return true;
};

/**
 * Creates an ISO 8601 timestamp from a date string and optional time string.
 * If time is not provided, uses the current time.
 * 
 * @param {string} date - Date in MM/DD/YYYY format
 * @param {string} [time] - Time in HH:mm format (optional, defaults to current time)
 * @returns {string} ISO 8601 timestamp string
 */
const createTimestamp = (date?: string, time?: string): string => {
  if (!date) return '';
  
  const timeToUse = time || dayjs().format('HH:mm:ss');
  
  // dayjs will auto-parse common formats including AM/PM
  return dayjs(`${date} ${timeToUse}`).toISOString();
};

const formatAddress = ({
  street_address_1,
  street_address_2,
  city,
  state,
  zip
}: {
  street_address_1: string;
  street_address_2: string | null;
  city: string;
  state: string;
  zip: string;
}) => {
  // First line: street address
  const streetAddress = [street_address_1, street_address_2]
    .filter(Boolean).join(' ');

  // Second line: city, state zip
  const cityStateZip = [city, state, zip]
    .filter(Boolean).join(', ');
    
    return { streetAddress, cityStateZip };
  };

const sleep = (ms: number) => {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
};

// Yield to the browser so React can paint user interface changes. This is
// used just before a CPU-intensive operation blocks the main thread. The
// 100ms delay gives animations enough time to render before being throttled
const yieldToBrowser = async () => await sleep(100);

const formatBoolean = (value: boolean | null | undefined): string => {
  if (value === null || value === undefined) return '';
  return value ? 'Yes' : 'No';
};

const parseIntParam = (params: URLSearchParams, key: string): number | null => {
  const value = parseInt(params.get(key) ?? '', 10);
  return isNaN(value) ? null : value;
};

const parseDateTimeParam = (params: URLSearchParams, key: string): Dayjs | null => {
  const value = params.get(key);
  return value ? dayjs(value) : null;
};

const parseArrayParam = (params: URLSearchParams, key: string): string[] => {
  const value = params.get(key);
  if (!value) return [];
  return value.split(',').filter(s => s.length > 0);
};

const capitalize = s => s[0]?.toUpperCase() + s.slice(1);

const getInitials = (firstName?: string, lastName?: string): string =>
  `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();

export default {
  nullIfEmpty,
  withArticle,
  parseCommaSeparatedList,
  truncate,
  getTodaysDate,
  fullName,
  areSetsEqual,
  createTimestamp,
  formatAddress,
  sleep,
  yieldToBrowser,
  formatBoolean,
  parseIntParam,
  parseDateTimeParam,
  parseArrayParam,
  capitalize,
  getInitials,
};
